import { NotificationHost } from '../components/notifications/NotificationCenter';
import React, { useEffect, useState } from 'react';
import { useLocation, useOutlet } from 'react-router-dom';
import { Sidebar } from '../components/Sidebar';
import { Topbar } from '../components/Topbar';
import { useAppStore } from '../store/useAppStore';
import { getPermissionApplyUrl } from '../lib/gatekeeperUrls';
import { hasAnyAdSitePermissionCode } from '../lib/adPermissions';
import { MarketingTableCopySupport } from '../components/MarketingTableCopySupport';
import { UsageRouteTracker } from '../components/UsageRouteTracker';
import { UsageActionTracker } from '../components/UsageActionTracker';
import { WorkspaceTabOutlet, WorkspaceTabs, WorkspaceTabsProvider } from '../components/WorkspaceTabs';

function LayoutSkeleton() {
  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div className="skeleton-card">
        <div className="skeleton" style={{ width: 220, height: 20, marginBottom: 14 }} />
        <div className="skeleton" style={{ width: '42%', height: 12, marginBottom: 10 }} />
        <div className="skeleton" style={{ width: '28%', height: 12 }} />
      </div>
      <div className="skeleton-card">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12, marginBottom: 18 }}>
          <div className="skeleton" style={{ height: 36 }} />
          <div className="skeleton" style={{ height: 36 }} />
          <div className="skeleton" style={{ height: 36 }} />
          <div className="skeleton" style={{ height: 36 }} />
        </div>
        <div className="skeleton" style={{ width: '100%', height: 44, marginBottom: 10 }} />
        <div className="skeleton" style={{ width: '100%', height: 44, marginBottom: 10 }} />
        <div className="skeleton" style={{ width: '100%', height: 44, marginBottom: 10 }} />
        <div className="skeleton" style={{ width: '100%', height: 44 }} />
      </div>
    </div>
  );
}

const isMarketingAnalysisTablePage = (pathname: string) =>
  pathname.startsWith('/ad-analysis/') || pathname.startsWith('/site-data/');

export const AppLayout: React.FC = () => {
  const fetchPermissions = useAppStore((s) => s.fetchPermissions);
  const isForbidden = useAppStore((s) => s.isForbidden);
  const permissionCodes = useAppStore((s) => s.permissionCodes);
  const permissionsLoaded = useAppStore((s) => s.permissionsLoaded);
  const permissionsCheckDone = useAppStore((s) => s.permissionsCheckDone);
  const sitesLoaded = useAppStore((s) => s.sitesLoaded);
  const location = useLocation();
  const outlet = useOutlet();

  // 权限已加载、有权限码、但无任何 ad:brand:site 码 → 整页拦截
  const noAdAccess =
    permissionsLoaded &&
    !isForbidden &&
    permissionCodes.length > 0 &&
    !hasAnyAdSitePermissionCode(permissionCodes) &&
    location.pathname !== '/system/notifications';

  // 权限检查完成 + 站点列表加载完成，才放行 Outlet
  // 用 store 标志代替 promise .finally()，确保 fetchSites 的竞态 siteId 修正也完成后再渲染
  const isNotificationPage = location.pathname === '/notifications';
  const ready = isNotificationPage || (permissionsCheckDone && sitesLoaded);
  const accessBlocked = !isNotificationPage && (isForbidden || noAdAccess);
  const [mobileOpen, setMobileOpen] = useState(false);

  // 路由切换时关闭移动端侧边栏
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    // 触发权限拉取；ready 状态由 store 的 permissionsCheckDone + sitesLoaded 驱动
    fetchPermissions();

    // 用户在 Gatekeeper 申请权限后切回来时，后台静默刷新权限（不阻塞页面）
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') fetchPermissions();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  useEffect(() => {
    // Chrome only treats the calendar icon as the picker trigger in some
    // layouts. Make the whole native date field a picker hot area so every
    // dashboard/date-range form behaves consistently.
    const openDatePicker = (event: PointerEvent) => {
      if (event.button !== 0 || event.defaultPrevented) return;
      const origin = event.target;
      if (!(origin instanceof Element)) return;
      const input = origin.closest('input[type="date"]');
      if (!(input instanceof HTMLInputElement) || input.disabled || input.readOnly) return;

      try {
        input.showPicker?.();
      } catch {
        // showPicker is not available in every browser; focusing preserves
        // the platform-native picker behavior as the fallback.
        input.focus({ preventScroll: true });
      }
    };

    document.addEventListener('pointerdown', openDatePicker);
    return () => document.removeEventListener('pointerdown', openDatePicker);
  }, []);


  // isForbidden is handled in the JSX below — no redirect needed

  return (
    <div className="app">
      <NotificationHost />
      <Sidebar mobileOpen={mobileOpen} />
      {/* 移动端遮罩 */}
      {mobileOpen && (
        <div
          className="sidebar-overlay"
          onClick={() => setMobileOpen(false)}
        />
      )}
      <WorkspaceTabsProvider enabled={ready && !accessBlocked} outlet={outlet}>
      <div className="main">
        <Topbar onMenuToggle={() => setMobileOpen((v) => !v)} />
        {ready && !accessBlocked ? <WorkspaceTabs /> : null}
        {ready && !accessBlocked ? <UsageRouteTracker /> : null}
        {ready && !accessBlocked ? <UsageActionTracker /> : null}
        <MarketingTableCopySupport enabled={isMarketingAnalysisTablePage(location.pathname)} />
        <div className="content">
          <div className={'content-inner' + (isMarketingAnalysisTablePage(location.pathname) ? ' marketing-analysis-content' : '')}>
            {!ready ? <LayoutSkeleton /> : accessBlocked ? (
              <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', height: '100%', minHeight: 400,
              }}>
                <div style={{ textAlign: 'center', maxWidth: 360 }}>
                  <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#FEF2F2', margin: '0 auto 16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#A32D2D" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 600, color: '#333', marginBottom: 8 }}>无访问权限</div>
                  {noAdAccess ? (
                    <>
                      <div style={{ fontSize: 14, color: '#888', marginBottom: 4 }}>你没有任何广告品牌 / 站点的数据访问权限</div>
                      <div style={{ fontSize: 13, color: '#AAA', marginBottom: 20 }}>请去权限管理平台申请对应品牌的广告数据权限</div>
                    </>
                  ) : (
                    <>
                      <div style={{ fontSize: 14, color: '#888', marginBottom: 4 }}>你还没有该页面的访问权限</div>
                      <div style={{ fontSize: 13, color: '#AAA', marginBottom: 20 }}>请前往系统管理平台提交权限申请</div>
                    </>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <a
                      href={getPermissionApplyUrl()}
                      target="_blank"
                      rel="noreferrer"
                      style={{ fontSize: 13, padding: '8px 20px', borderRadius: 6, background: '#1210B8', color: '#fff', border: 'none', textDecoration: 'none', cursor: 'pointer' }}
                    >
                      申请权限
                    </a>
                  </div>
                </div>
              </div>
            ) : (
              <WorkspaceTabOutlet />
            )}
          </div>
          <footer className="page-footer">
            泽鹿数字科技 · 技术研发部 出品 © {new Date().getFullYear()}
          </footer>
        </div>
      </div>
      </WorkspaceTabsProvider>
    </div>
  );
};

export default AppLayout;
